// §25 phase 2: passkey registration + login. The one place this app calls
// navigator.credentials – everything else uses Datastar's data-on:click plus
// plain fetch/form-post. Kept as its own small bundle (see esbuild config)
// rather than folded into another module, since it's a distinct concern loaded
// on different pages (Settings, and the rare all-profiles-protected login page).

// pkMsg reads a localized error string from the #webauthn-i18n element the server
// renders on every page that loads this bundle (§56), falling back to English.
function pkMsg(key: string, fallback: string): string {
  const el = document.getElementById('webauthn-i18n');
  const v = el && el.getAttribute('data-' + key);
  return v || fallback;
}

// §117: a passkey login started from an authorization request has to return to it,
// exactly as the password path does -- otherwise you sign in, land on the dashboard,
// and the assistant waits forever on a callback that never comes. Same filter as the
// server's safeNext: a path on this site only, never a scheme-relative one, which a
// browser would treat as absolute.
function returnTo(): string {
  const next = new URLSearchParams(location.search).get('next');
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

async function passkeyRegister(profileId: string, label: string): Promise<void> {
  const beginResp = await fetch(`/api/profiles/${profileId}/passkey/register/begin`, { method: 'POST' });
  if (!beginResp.ok) {
    window.__notify(pkMsg('err-register-start', 'Passkey registration could not be started.'), 'error');
    return;
  }
  const options = await beginResp.json();
  const creationOptions = PublicKeyCredential.parseCreationOptionsFromJSON(options.publicKey);

  let credential: PublicKeyCredential;
  try {
    credential = (await navigator.credentials.create({ publicKey: creationOptions })) as PublicKeyCredential;
  } catch {
    return; // user cancelled or the platform rejected it – no error needed
  }

  const finishResp = await fetch(
    `/api/profiles/${profileId}/passkey/register/finish?label=${encodeURIComponent(label)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credential.toJSON()),
    },
  );
  if (finishResp.ok) {
    location.reload();
  } else {
    window.__notify(pkMsg('err-save', 'Passkey could not be saved.'), 'error');
  }
}

async function passkeyLogin(profileId: string): Promise<void> {
  const beginResp = await fetch(`/api/profiles/${profileId}/passkey/login/begin`, { method: 'POST' });
  if (!beginResp.ok) {
    window.__notify(pkMsg('err-none-profile', 'No passkeys registered for this profile.'), 'warn');
    return;
  }
  const options = await beginResp.json();
  const requestOptions = PublicKeyCredential.parseRequestOptionsFromJSON(options.publicKey);

  let assertion: PublicKeyCredential;
  try {
    assertion = (await navigator.credentials.get({ publicKey: requestOptions })) as PublicKeyCredential;
  } catch {
    return; // user cancelled – no error needed
  }

  const finishResp = await fetch(`/api/profiles/${profileId}/passkey/login/finish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(assertion.toJSON()),
  });
  if (finishResp.ok) {
    location.href = returnTo();
  } else {
    window.__notify(pkMsg('err-login', 'Passkey login failed.'), 'error');
  }
}

// §6 SaaS account-level counterparts to the two functions above: same
// ceremony shape, but scoped to the session-authenticated account
// (register) or an email typed into the login form (login) rather than a
// profileId already known from the page URL.

async function passkeyRegisterAccount(label: string): Promise<void> {
  const beginResp = await fetch('/account/passkey/register/begin', { method: 'POST' });
  if (!beginResp.ok) {
    window.__notify(pkMsg('err-register-start', 'Passkey registration could not be started.'), 'error');
    return;
  }
  const options = await beginResp.json();
  const creationOptions = PublicKeyCredential.parseCreationOptionsFromJSON(options.publicKey);

  let credential: PublicKeyCredential;
  try {
    credential = (await navigator.credentials.create({ publicKey: creationOptions })) as PublicKeyCredential;
  } catch {
    return; // user cancelled or the platform rejected it – no error needed
  }

  const finishResp = await fetch(
    `/account/passkey/register/finish?label=${encodeURIComponent(label)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credential.toJSON()),
    },
  );
  if (finishResp.ok) {
    location.reload();
  } else {
    window.__notify(pkMsg('err-save', 'Passkey could not be saved.'), 'error');
  }
}

// passkeyLoginAccount resolves accountId server-side from email (typed into
// the login form) rather than a URL param -- there is no known account yet
// at this point, unlike the profile switcher's already-chosen profileId.
async function passkeyLoginAccount(email: string): Promise<void> {
  const beginResp = await fetch('/auth/login/passkey/begin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: `email=${encodeURIComponent(email)}`,
  });
  if (!beginResp.ok) {
    window.__notify(pkMsg('err-none-email', 'No passkey available for this email.'), 'warn');
    return;
  }
  const options = await beginResp.json();
  const requestOptions = PublicKeyCredential.parseRequestOptionsFromJSON(options.publicKey);

  let assertion: PublicKeyCredential;
  try {
    assertion = (await navigator.credentials.get({ publicKey: requestOptions })) as PublicKeyCredential;
  } catch {
    return; // user cancelled – no error needed
  }

  const finishResp = await fetch(`/auth/login/passkey/finish?email=${encodeURIComponent(email)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(assertion.toJSON()),
  });
  if (finishResp.ok) {
    location.href = '/';
  } else {
    window.__notify(pkMsg('err-login', 'Passkey login failed.'), 'error');
  }
}

declare global {
  interface Window {
    __passkeyRegister: typeof passkeyRegister;
    __passkeyLogin: typeof passkeyLogin;
    __passkeyRegisterAccount: typeof passkeyRegisterAccount;
    __passkeyLoginAccount: typeof passkeyLoginAccount;
  }
}
window.__passkeyRegister = passkeyRegister;
window.__passkeyLogin = passkeyLogin;
window.__passkeyRegisterAccount = passkeyRegisterAccount;
window.__passkeyLoginAccount = passkeyLoginAccount;

export {};
